/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './GovernmentWatch.module.scss';

type Translate = (key: string) => string;

const CATEGORY_ICONS = {
  ownership: (
    <>
      <path d="M12 3.5v8.5h8.5" />
      <path d="M20.3 13.5A8.5 8.5 0 1 1 12 3.2" />
    </>
  ),
  officers: (
    <>
      <circle cx="12" cy="8.3" r="3.6" />
      <path d="M4.8 20c0-4 3.3-6.7 7.2-6.7s7.2 2.7 7.2 6.7" strokeLinecap="round" />
    </>
  ),
  standing: <path d="M12 3.5 19.5 6.5V12c0 4.6-3.1 7.9-7.5 9-4.4-1.1-7.5-4.4-7.5-9V6.5z" strokeLinejoin="round" />,
  identity: (
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2.2" />
      <circle cx="8.4" cy="10.8" r="1.7" />
      <path d="M5.6 15.6c.4-1.5 1.5-2.3 2.8-2.3s2.4.8 2.8 2.3M13.5 9.7h5M13.5 13h5" strokeLinecap="round" />
    </>
  ),
  financial: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path
        d="M12 7.5v9M14.6 9.4c-.5-.7-1.5-1.1-2.6-1.1-1.5 0-2.7.8-2.7 2s1.2 1.7 2.7 1.9c1.5.2 2.7.8 2.7 2s-1.2 2-2.7 2c-1.1 0-2.1-.4-2.6-1.1"
        strokeLinecap="round"
      />
    </>
  ),
  screening: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4-4" strokeLinecap="round" />
      <path d="M11 7.5V11l2.4 1.6" strokeLinecap="round" />
    </>
  ),
} as const;

type Category = {
  id: string;
  name: string;
  icon: keyof typeof CATEGORY_ICONS;
  items: string[];
};

const getCategories = (t: Translate): Category[] => [
  {
    id: 'ownership',
    name: t('Ownership'),
    icon: 'ownership',
    items: [
      t('New shareholder filed'),
      t('Shareholder exited'),
      t('Shareholding percentage changed'),
      t('Ownership chain restructured'),
      t('New intermediate holding company'),
      t('Ultimate beneficial owner changed'),
      t('Person with significant control added'),
      t('Person with significant control ceased'),
    ],
  },
  {
    id: 'officers',
    name: t('Officers'),
    icon: 'officers',
    items: [
      t('Director appointed'),
      t('Director resigned'),
      t('Secretary appointed'),
      t('Secretary resigned'),
      t('Officer role changed'),
      t('Officer service address changed'),
    ],
  },
  {
    id: 'standing',
    name: t('Standing'),
    icon: 'standing',
    items: [
      t('Company status changed'),
      t('Strike-off action started'),
      t('Strike-off action discontinued'),
      t('Liquidation recorded'),
      t('Administration recorded'),
      t('Dissolution recorded'),
      t('Restored to the register'),
    ],
  },
  {
    id: 'identity',
    name: t('Identity'),
    icon: 'identity',
    items: [
      t('Legal name changed'),
      t('Previous name recorded'),
      t('Registered address changed'),
      t('Company type changed'),
      t('VAT number changed'),
      t('VAT registration cancelled'),
    ],
  },
  {
    id: 'financial',
    name: t('Financial'),
    icon: 'financial',
    items: [
      t('Annual accounts filed'),
      t('Accounts overdue'),
      t('Accounting reference date changed'),
      t('Charge registered'),
      t('Charge satisfied'),
      t('Confirmation statement filed'),
    ],
  },
  {
    id: 'screening',
    name: t('Screening'),
    icon: 'screening',
    items: [
      t('Sanctions designation added'),
      t('Sanctions designation removed'),
      t('PEP status recorded on a graph node'),
      t('Ownership-based threshold crossed'),
      t('State ownership recorded'),
    ],
  },
];

const TOTAL = 38;

export const GovernmentWatch = () => {
  const { t } = useInt();
  const categories = getCategories(t);
  const gridRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(gridRef, { threshold: 0.15 });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    const steps = 26;
    let step = 0;
    const id = setInterval(() => {
      step += 1;
      setCount(Math.round(TOTAL * Math.min(step / steps, 1)));
      if (step >= steps) {
        clearInterval(id);
      }
    }, 24);

    return () => clearInterval(id);
  }, [revealed]);

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={
            <>
              {count} {t('change types, on every entity and on every owner above it.')}
            </>
          }
          description={t(
            "Each one is a filing at a source register, not a difference between two snapshots. An alert means the company actually changed — a share moved, an officer was appointed, a designation was added — rather than a record being reformatted somewhere in a vendor's pipeline.",
          )}
        />
      </div>

      <div className={cn(styles.grid, revealed && styles.gridOn)} ref={gridRef}>
        {categories.map((cat, i) => (
          <div className={styles.card} style={{ '--d': `${i * 60}ms` } as CSSProperties} key={cat.id}>
            <div className={styles.cardHead}>
              <span className={styles.cardIcon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {CATEGORY_ICONS[cat.icon]}
                </svg>
              </span>
              <span className={styles.cardName}>{cat.name}</span>
              <span className={styles.cardCount}>{cat.items.length}</span>
            </div>
            <div className={styles.cardBody}>
              <ul>
                {cat.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.cta}>
        <div className={styles.ctaCopy}>
          <span className={styles.ctaKicker}>{t('Portfolio scan')}</span>
          <h3 className={styles.ctaHeading}>
            {t('Send us your supplier register. We will tell you what has changed since you last looked.')}
          </h3>
        </div>
        <Button type="blue" size="xl" href="/contact-us" suffixIcon="arrow-forward">
          {t('Request a portfolio scan')}
        </Button>
      </div>
    </Section>
  );
};
